import type { JSX } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconPlus, IconTrash, IconFile, IconMessage, IconSpark, IconChart, IconFlag,
  IconClock, IconHeadset, IconSettings, IconArrowRight
} from "../icons";
import { BrandLogo } from "../BrandLogo";
import { fetchTickets } from "../../lib/api";
import type { ThreadSummary } from "@shared/types";
import type { AuthUser } from "../../lib/auth";

interface SidebarProps {
  threads: ThreadSummary[];
  activeThreadId: string | null;
  onSelectThread: (id: string) => void;
  onNewThread: () => void;
  onDeleteThread: (id: string) => void;
  onOpenTickets: () => void;
  /** Logged-in user (null when auth is disabled). */
  user: AuthUser | null;
  onLogout: () => void;
  onOpenFeedback: () => void;
  /** 使用手册 / Guides reader surface (FEATURE_MANUAL_READER). Hidden when off. */
  manualReaderEnabled?: boolean;
  /** Sidebar label for the manual entry — carries the configured product name
   *  (e.g. "使用手册 · 织灵"), so it reflects WHICH product's manual is shown. */
  manualLabel?: string;
  /** Meeting capture surface (FEATURE_MEETING + MEETING_CAPTURE). Hidden when off. */
  meetingEnabled?: boolean;
  onOpenMeetings?: () => void;
  /** Highlight the 会议 entry while the meeting view is the active surface. */
  meetingActive?: boolean;
  /** /settings audit surface (FEATURE_SETTINGS). Hidden when off. */
  settingsEnabled?: boolean;
}

// Sidebar layout (design: docs/design/2026-07-10-sidebar-reorg-mock.html):
// only CONTENT (资料与能力 / 最近) and IDENTITY (account) live permanently in
// the rail. Admin tools sit under a role-gated 管理 label; 我的工单/设置/退出
// fold into the account popover (open tickets surface as a dot on the account
// row); 意见反馈 keeps today's link style in a standalone slot — prominent by
// position, not by louder treatment (Alpha: feedback volume is a goal).

// Same remembered-email key as TicketsPanel — the open-ticket count follows
// the identical "my tickets" scoping; without an email there is no count.
const EMAIL_KEY = "cyclecare_email";

function formatRelative(ts: number): string {
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "刚刚";
  if (min < 60) return `${min} 分钟前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小时前`;
  return `${Math.floor(hr / 24)} 天前`;
}

export function Sidebar({
  threads,
  activeThreadId,
  onSelectThread,
  onNewThread,
  onDeleteThread,
  onOpenTickets,
  user,
  onLogout,
  onOpenFeedback,
  manualReaderEnabled = false,
  manualLabel = "使用手册",
  meetingEnabled = false,
  onOpenMeetings,
  meetingActive = false,
  settingsEnabled = false
}: SidebarProps): JSX.Element {
  const navigate = useNavigate();

  // Account popover (我的工单 / 设置 / 退出). Closes on Esc and click-outside.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    const onDown = (e: MouseEvent): void => {
      if (menuRef.current !== null && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [menuOpen]);

  const [openTickets, setOpenTickets] = useState(0);
  useEffect(() => {
    if (user === null) return;
    const email = localStorage.getItem(EMAIL_KEY) ?? "";
    if (email.trim().length === 0) return;
    fetchTickets(email.trim())
      .then((r) => setOpenTickets(r.tickets.filter((t) => t.state === "open").length))
      .catch(() => setOpenTickets(0));
  }, [user]);

  const showCapabilities = manualReaderEnabled || (meetingEnabled && onOpenMeetings !== undefined);
  const isStaff = user?.role === "admin" || user?.role === "agent";

  const openFromMenu = useCallback((fn: () => void) => {
    setMenuOpen(false);
    fn();
  }, []);

  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <BrandLogo size={26} />
        <span className="sidebar-brand-name">CycleCare</span>
        <span className="sidebar-brand-zh">小环</span>
        <span className="sidebar-brand-alpha" title="Alpha 版本">
          <IconSpark size={11} />
          Alpha
        </span>
      </div>
      <div className="sidebar-top">
        <button className="new-btn" onClick={onNewThread} type="button">
          <IconPlus size={14} />
          新建对话
        </button>
      </div>
      {showCapabilities && (
        // 资料与能力 — product capabilities live near the top, not in the
        // bottom tool pile. Only real entries render (no dead links).
        <div className="sidebar-materials">
          <div className="sidebar-label">资料与能力</div>
          {manualReaderEnabled && (
            <button className="sidebar-link mat" type="button" onClick={() => navigate("/guides")}>
              <IconFile size={14} />
              {manualLabel}
            </button>
          )}
          {meetingEnabled && onOpenMeetings !== undefined && (
            <button
              className={`sidebar-link mat${meetingActive ? " active" : ""}`}
              type="button"
              onClick={onOpenMeetings}
            >
              <IconHeadset size={14} />
              会议
            </button>
          )}
        </div>
      )}
      <div className="sidebar-scroll">
        <div className="sidebar-label">最近</div>
        {threads.length === 0 ? (
          <div className="sidebar-empty">还没有对话</div>
        ) : (
          threads.map((t) => (
            <div key={t.id} className={`nav-item ${t.id === activeThreadId ? "active" : ""}`}>
              <button type="button" className="nav-item-main" onClick={() => onSelectThread(t.id)}>
                <span className="nav-item-title">{t.title}</span>
                <span className="nav-item-time">
                  {formatRelative(t.lastTurnAt)} ·{" "}
                  {t.handedOff === true ? "已转人工" : `${t.turnCount} 轮`}
                </span>
              </button>
              <button
                type="button"
                className="nav-item-del"
                aria-label="删除会话"
                title="删除会话"
                onClick={() => onDeleteThread(t.id)}
              >
                <IconTrash size={14} />
              </button>
            </div>
          ))
        )}
      </div>
      <div className="sidebar-bottom">
        {isStaff && (
          <>
            <div className="sidebar-label">管理</div>
            {user?.role === "admin" && (
              <button className="sidebar-link" type="button" onClick={() => navigate("/admin")}>
                <IconChart size={14} />
                控制台
              </button>
            )}
            <button className="sidebar-link" type="button" onClick={() => navigate("/review")}>
              <IconFlag size={14} />
              标注台
            </button>
            {user?.role === "admin" && (
              <button className="sidebar-link" type="button" onClick={() => navigate("/scheduler")}>
                <IconClock size={14} />
                定时任务
              </button>
            )}
          </>
        )}
        <button className="sidebar-link" type="button" onClick={onOpenFeedback}>
          <IconMessage size={14} />
          意见反馈
        </button>
        {user !== null ? (
          <div className="sidebar-account-wrap" ref={menuRef}>
            {menuOpen && (
              <div className="sidebar-pop" role="menu">
                <button className="sidebar-link" type="button" role="menuitem" onClick={() => openFromMenu(onOpenTickets)}>
                  <IconFile size={14} />
                  我的工单
                  {openTickets > 0 && <span className="sidebar-badge">{openTickets}</span>}
                </button>
                {settingsEnabled && (
                  <button className="sidebar-link" type="button" role="menuitem" onClick={() => openFromMenu(() => navigate("/settings"))}>
                    <IconSettings size={14} />
                    设置
                  </button>
                )}
                <div className="sidebar-pop-sep" />
                <button className="sidebar-link" type="button" role="menuitem" onClick={() => openFromMenu(onLogout)}>
                  <IconArrowRight size={14} style={{ transform: "rotate(180deg)" }} />
                  退出登录
                </button>
              </div>
            )}
            <button
              className="sidebar-account sidebar-account-btn"
              type="button"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span className="sidebar-account-name" title={`${user.username} · ${user.tenant}`}>
                {user.username}
                <span className="sidebar-account-tenant"> · {user.tenant}</span>
              </span>
              {openTickets > 0 && <span className="sidebar-dot-unread" title="有进行中的工单" />}
              <span className="sidebar-chev" aria-hidden="true">▾</span>
            </button>
          </div>
        ) : (
          // Auth disabled: no account row to host the popover — keep direct
          // entries so 工单/设置 stay reachable.
          <>
            <button className="sidebar-link" type="button" onClick={onOpenTickets}>
              <IconFile size={14} />
              我的工单
            </button>
            {settingsEnabled && (
              <button className="sidebar-link" type="button" onClick={() => navigate("/settings")}>
                <IconSettings size={14} />
                设置
              </button>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
